import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Checkbox,
  Typography,
  Box,
  CircularProgress,
  Chip,
  Popper,
  Paper,
  ClickAwayListener,
} from '@mui/material';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import { getDataWAuth } from '../../../utils/api-service';
import styles from './DealerTrendsAddModal.module.css';
import { CCM_DASHBOARD_DEALER } from '../../../constants/constant';

const MAX_DEALERS = 5;

const DealerTrendsAddModal = ({ open, onClose, onConfirm, currentDealers = [] }) => {
  const [selectedDealers, setSelectedDealers] = useState([]);
  const [dealers, setDealers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);

  // Fetch dealers from API when modal opens
  useEffect(() => {
    if (open) {
      fetchDealers();
      setSelectedDealers([...currentDealers]);
    }
  }, [open]);

  const fetchDealers = async () => {
    setLoading(true);
    try {
      const response = await getDataWAuth(CCM_DASHBOARD_DEALER);
      const dealersData = response?.data || response || [];
      setDealers(Array.isArray(dealersData) ? dealersData : []);
    } catch (error) {
      console.error('Error fetching dealers:', error);
      setDealers([]);
    } finally {
      setLoading(false);
    }
  };

  const handleDropdownToggle = (event) => {
    setAnchorEl(event.currentTarget);
    setDropdownOpen(!dropdownOpen);
  };

  const handleDropdownClose = () => {
    setDropdownOpen(false);
  };

  const handleDealerToggle = (dealer) => {
    const isSelected = selectedDealers.find((d) => d.dealerId === dealer.dealerId);

    if (isSelected) {
      // Remove dealer
      setSelectedDealers(selectedDealers.filter((d) => d.dealerId !== dealer.dealerId));
    } else {
      // Check if we can add more dealers
      if (selectedDealers.length >= MAX_DEALERS) {
        return; // Don't add if limit reached
      }
      setSelectedDealers([...selectedDealers, dealer]);
    }
  };

  const handleRemoveDealer = (dealerId) => {
    setSelectedDealers(selectedDealers.filter((d) => d.dealerId !== dealerId));
  };

  const handleConfirm = () => {
    onConfirm(selectedDealers);
    setSelectedDealers([]);
    setDropdownOpen(false);
  };

  const handleCancel = () => {
    setSelectedDealers([]);
    setDropdownOpen(false);
    onClose();
  };

  const handleClearAll = () => {
    setSelectedDealers([]);
  };

  const canSelectMore = selectedDealers.length < MAX_DEALERS;

  return (
    <Dialog open={open} onClose={handleCancel} maxWidth="sm" fullWidth>
      <DialogTitle className={styles.dialogTitle}>Add Dealer</DialogTitle>
      <DialogContent className={styles.dialogContent}>
        {/* Dropdown Field */}
        <Box className={styles.dropdownContainer}>
          <Box
            className={styles.dropdownField}
            onClick={handleDropdownToggle}
            ref={setAnchorEl}
          >
            <Typography className={styles.dropdownText}>
              {selectedDealers.length} dealers selected
            </Typography>
            <KeyboardArrowDownIcon className={styles.dropdownIcon} />
          </Box>

          {/* Dropdown Menu */}
          <Popper
            open={dropdownOpen}
            anchorEl={anchorEl}
            placement="bottom-start"
            style={{ width: anchorEl?.offsetWidth, zIndex: 1500 }}
          >
            <ClickAwayListener onClickAway={handleDropdownClose}>
              <Paper className={styles.dropdownMenu}>
                {loading ? (
                  <Box className={styles.loadingBox}>
                    <CircularProgress size={30} />
                    <Typography variant="body2" className={styles.loadingText}>
                      Loading dealers...
                    </Typography>
                  </Box>
                ) : dealers.length === 0 ? (
                  <Box className={styles.emptyState}>
                    <Typography variant="body2" color="textSecondary">
                      No dealers available
                    </Typography>
                  </Box>
                ) : (
                  <Box className={styles.dealerList}>
                    {dealers.map((dealer) => {
                      const isSelected = selectedDealers.find((d) => d.dealerId === dealer.dealerId);
                      const isDisabled = !isSelected && !canSelectMore;

                      return (
                        <Box
                          key={dealer.dealerId}
                          className={`${styles.dealerItem} ${isSelected ? styles.selected : ''} ${
                            isDisabled ? styles.disabled : ''
                          }`}
                          onClick={() => !isDisabled && handleDealerToggle(dealer)}
                        >
                          <Checkbox
                            checked={!!isSelected}
                            disabled={isDisabled}
                            className={styles.checkbox}
                            onClick={(e) => e.stopPropagation()}
                            onChange={() => !isDisabled && handleDealerToggle(dealer)}
                          />
                          <Typography className={styles.dealerLabel}>{dealer.dealerName}</Typography>
                        </Box>
                      );
                    })}
                  </Box>
                )}
              </Paper>
            </ClickAwayListener>
          </Popper>
        </Box>

        {/* Selected Dealers as Chips */}
        {selectedDealers.length > 0 && (
          <Box className={styles.chipsContainer}>
            {selectedDealers.map((dealer) => (
              <Chip
                key={dealer.dealerId}
                label={dealer.dealerName}
                onDelete={() => handleRemoveDealer(dealer.dealerId)}
                className={styles.dealerChip}
              />
            ))}
          </Box>
        )}

        {/* Clear All Link */}
        {selectedDealers.length > 0 && (
          <Box className={styles.footer}>
            <Typography variant="caption" className={styles.clearLink} onClick={handleClearAll}>
              Clear All
            </Typography>
          </Box>
        )}
      </DialogContent>
      <DialogActions className={styles.dialogActions}>
        <Button onClick={handleCancel} variant="outlined" className={styles.cancelButton}>
          Cancel
        </Button>
        <Button
          onClick={handleConfirm}
          variant="contained"
          className={styles.confirmButton}
        >
          Confirm
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default DealerTrendsAddModal;
